<script setup lang="ts">
import { authUserHasAnyRole, authUserHasRole } from '@/helpers/globals';
import { ROLES } from '@/models/Rolle';
import type { MenuItem } from 'primevue/menuitem';
import PanelMenu, { type PanelMenuExpandedKeys } from 'primevue/panelmenu';
import { onMounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, type RouteRecordNameGeneric } from 'vue-router';

const expandedKeys: Ref<PanelMenuExpandedKeys> = ref({});

const menu: Ref<MenuItem[]> = ref([
    {
        label: useI18n().t('berechnungsgrundlagen'),
        visible: authUserHasAnyRole([
            ROLES.berechnungsgrundlagen_allgemein,
            ROLES.berechnungsgrundlagen_gebaeude,
            ROLES.berechnungsgrundlagen_produktion,
            ROLES.berechnungsgrundlagen_jahreswerte,
        ]),
        icon: 'fa-solid fa-clipboard-list',
        key: 'bg',
        items: [
            {
                label: useI18n().t('allgemein'),
                visible: authUserHasRole(ROLES.berechnungsgrundlagen_allgemein),
                key: 'bg_allg',
                class: 'nav-layer-2',
                icon: 'fa-solid fa-location',
                items: [
                    {
                        route: 'bg_allg_gemeinde',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_allgemein),
                        key: 'bg_allg_gemeinde',
                        label: useI18n().t('gemeinden'),
                    },
                    {
                        route: 'bg_allg_nutzungsart',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_allgemein),
                        key: 'bg_allg_nutzungsart',
                        label: useI18n().t('nutzungsarten'),
                    },
                ],
            },
            {
                label: useI18n().t('gebaeude'),
                visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                key: 'bg_geb',
                class: 'nav-layer-2',
                icon: 'fa-solid fa-house',
                items: [
                    {
                        route: 'bg_geb_zuordnung_energie_waermequelle',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_zuordnung_energie_waermequelle',
                        label: useI18n().t('zuordnung_energie_waermequelle'),
                    },
                    {
                        route: 'bg_geb_waermeerzeuger_heizung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_waermeerzeuger_heizung',
                        label: useI18n().t('waermeerzeuger_heizung'),
                    },
                    {
                        route: 'bg_geb_zuordnung_waermeerzeuger_heizung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_zuordnung_waermeerzeuger_heizung',
                        label: useI18n().t('zuordnung_waermeerzeuger_heizung'),
                    },
                    {
                        route: 'bg_geb_waermeerzeuger_warmwasser',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_waermeerzeuger_warmwasser',
                        label: useI18n().t('waermeerzeuger_warmwasser'),
                    },
                    {
                        route: 'bg_geb_zuordnung_waermeerzeuger_warmwasser',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_zuordnung_waermeerzeuger_warmwasser',
                        label: useI18n().t('zuordnung_waermeerzeuger_warmwasser'),
                    },
                    {
                        route: 'bg_geb_korrektur_waermeerzeuger_heizung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_korrektur_waermeerzeuger_heizung',
                        label: useI18n().t('korr_waermeerzeuger_heizung'),
                    },
                    {
                        route: 'bg_geb_korrektur_waermeerzeuger_warmwasser',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_korrektur_waermeerzeuger_warmwasser',
                        label: useI18n().t('korr_waermeerzeuger_warmwasser'),
                    },
                    {
                        route: 'bg_geb_anteil_waermeerzeuger_heizung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_anteil_waermeerzeuger_heizung',
                        label: useI18n().t('anteile_waermeerzeuger_heizung'),
                    },
                    {
                        route: 'bg_geb_anteil_waermeerzeuger_warmwasser',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_anteil_waermeerzeuger_warmwasser',
                        label: useI18n().t('anteile_waermeerzeuger_warmwasser'),
                    },
                    {
                        route: 'bg_geb_nutzungsgrad_heizung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_nutzungsgrad_heizung',
                        label: useI18n().t('nutzungsgrade_heizung'),
                    },
                    {
                        route: 'bg_geb_nutzungsgrad_warmwasser',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_nutzungsgrad_warmwasser',
                        label: useI18n().t('nutzungsgrade_warmwasser'),
                    },
                    {
                        route: 'bg_geb_gebaeudeklasse_nutzungsart',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_gebaeudeklasse_nutzungsart',
                        label: useI18n().t('zuordnung_nutzungsart'),
                    },
                    {
                        route: 'bg_geb_kennzahl_nutzungsart',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_kennzahl_nutzungsart',
                        label: useI18n().t('kennzahlen_nutzungsarten'),
                    },
                    {
                        route: 'bg_geb_reduktion_angrenzende_gebaeude',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_reduktion_angrenzende_gebaeude',
                        label: useI18n().t('red_angerenzende_gebaeude'),
                    },
                    {
                        route: 'bg_geb_reduktion_waermeerzeuger',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_reduktion_waermeerzeuger',
                        label: useI18n().t('red_waermeerzeuger'),
                    },
                    {
                        route: 'bg_geb_ho_zu_hu',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_gebaeude),
                        key: 'bg_geb_ho_zu_hu',
                        label: useI18n().t('umrechnungsfaktor_ho_zu_hu'),
                    },
                ],
            },
            {
                label: useI18n().t('produktion'),
                visible: authUserHasRole(ROLES.berechnungsgrundlagen_produktion),
                key: 'bg_prod',
                class: 'nav-layer-2',
                icon: 'fa-solid fa-industry',
                items: [
                    {
                        route: 'bg_prod_standardgemeinde',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_produktion),
                        key: 'bg_prod_standardgemeinde',
                        label: useI18n().t('standardgemeinden'),
                    },
                    {
                        route: 'bg_prod_technologie_stromerzeugung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_produktion),
                        key: 'bg_prod_technologie_stromerzeugung',
                        label: useI18n().t('technologien_stromerzeugung'),
                    },
                    {
                        route: 'bg_prod_zuordnung_technologie_stromerzeugung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_produktion),
                        key: 'bg_prod_zuordnung_technologie_stromerzeugung',
                        label: useI18n().t('zuordnung_technologien'),
                    },
                ],
            },
            {
                label: useI18n().t('jahreswerte'),
                visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                key: 'bg_jw',
                class: 'nav-layer-2',
                icon: 'fa-solid fa-calendar-days',
                items: [
                    {
                        route: 'bg_jw_einwohnerzahl',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_einwohnerzahl',
                        label: useI18n().t('einwohnerzahlen'),
                    },
                    {
                        route: 'bg_jw_erdgasverbrauch',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_erdgasverbrauch',
                        label: useI18n().t('erdgasverbrauch'),
                    },
                    {
                        route: 'bg_jw_elektrizitaetsverbrauch',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_elektrizitaetsverbrauch',
                        label: useI18n().t('elektrizitaetsverbrauch'),
                    },
                    {
                        route: 'bg_jw_einspeisung_biogas',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_einspeisung_biogas',
                        label: useI18n().t('einspeisung_biogas'),
                    },
                    {
                        route: 'bg_jw_leitungsverlust_fernwaerme',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_leitungsverlust_fernwaerme',
                        label: useI18n().t('leitungsverlust_fernwaerme'),
                    },
                    {
                        route: 'bg_jw_standard_fernwaermemix',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_standard_fernwaermemix',
                        label: useI18n().t('standard_fernwaermemix'),
                    },
                    {
                        route: 'bg_jw_hgt_und_atd',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_hgt_und_atd',
                        label: useI18n().t('hgt_und_atd'),
                    },
                    {
                        route: 'bg_jw_co2_emissionsfaktor',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_co2_emissionsfaktor',
                        label: useI18n().t('co2_emissionsfaktoren'),
                    },
                    {
                        route: 'bg_jw_korrektur_kalibrierungsfaktor',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_korrektur_kalibrierungsfaktor',
                        label: useI18n().t('korrektur_kalibrierungsfaktor'),
                    },
                    {
                        route: 'bg_jw_jahresertrag_solarstromanlage',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_jahresertrag_solarstromanlage',
                        label: useI18n().t('jahresertrag_solarstromanlagen'),
                    },
                    {
                        route: 'bg_jw_korrektur_werte_stromerzeugung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_korrektur_werte_stromerzeugung',
                        label: useI18n().t('korrekturen_produktion'),
                    },
                    {
                        route: 'bg_jw_ergaenzung_werte_stromerzeugung',
                        visible: authUserHasRole(ROLES.berechnungsgrundlagen_jahreswerte),
                        key: 'bg_jw_ergaenzung_werte_stromerzeugung',
                        label: useI18n().t('ergaenzungen_produktion'),
                    },
                ],
            },
        ],
    },
    {
        label: useI18n().t('datenimport'),
        visible: authUserHasRole(ROLES.datenimport),
        icon: 'fa-solid fa-file-import',
        key: 'di',
        items: [
            {
                label: useI18n().t('gebaeude'),
                visible: authUserHasRole(ROLES.datenimport),
                icon: 'fa-solid fa-house',
                key: 'di_geb',
                class: 'nav-layer-2',
                items: [
                    {
                        route: 'di_geb_gwr_daten',
                        visible: authUserHasRole(ROLES.datenimport),
                        key: 'di_geb_gwr_daten',
                        label: useI18n().t('gwr_daten'),
                    },
                    {
                        route: 'di_geb_angrenzende_geb',
                        visible: authUserHasRole(ROLES.datenimport),
                        key: 'di_geb_angrenzende_geb',
                        label: useI18n().t('angrenzende_gebaeude'),
                    },
                    {
                        route: 'di_geb_grossverbraucher',
                        visible: authUserHasRole(ROLES.datenimport),
                        key: 'di_geb_grossverbraucher',
                        label: useI18n().t('grossverbraucher'),
                    },
                ],
            },
            {
                label: useI18n().t('produktion'),
                visible: authUserHasRole(ROLES.datenimport),
                icon: 'fa-solid fa-industry',
                key: 'di_prod',
                class: 'nav-layer-2',
                items: [
                    {
                        route: 'di_prod_eingespeiste_energie',
                        visible: authUserHasRole(ROLES.datenimport),
                        key: 'di_prod_eingespeiste_energie',
                        label: useI18n().t('eingespeiste_energie'),
                    },
                    {
                        route: 'di_prod_produktionseinheiten',
                        visible: authUserHasRole(ROLES.datenimport),
                        key: 'di_prod_produktionseinheiten',
                        label: useI18n().t('produktionseinheiten'),
                    },
                    {
                        route: 'di_prod_solarstromanlagen',
                        visible: authUserHasRole(ROLES.datenimport),
                        key: 'di_prod_solarstromanlagen',
                        label: useI18n().t('solarstromanlagen'),
                    },
                ],
            },
        ],
    },
    {
        label: useI18n().t('berechnungen'),
        visible: authUserHasAnyRole([ROLES.berechnungen, ROLES.berechnete_listen]),
        icon: 'fa-solid fa-calculator',
        key: 'ber',
        items: [
            {
                label: useI18n().t('status_berechnungen'),
                visible: authUserHasRole(ROLES.berechnungen),
                key: 'ber_import_status',
                class: 'selectable-header',
                icon: 'fa-solid fa-chart-simple-horizontal',
                route: 'ber_import_status',
            },
            {
                label: useI18n().t('gebaeude'),
                visible: authUserHasRole(ROLES.berechnete_listen),
                icon: 'fa-solid fa-house',
                key: 'ber_geb',
                class: 'nav-layer-2',
                items: [
                    {
                        route: 'ber_geb_list',
                        visible: authUserHasRole(ROLES.berechnete_listen),
                        key: 'ber_geb_list',
                        label: useI18n().t('gebaeude_liste'),
                    },
                    {
                        route: 'ber_geb_ev',
                        visible: authUserHasRole(ROLES.berechnete_listen),
                        key: 'ber_geb_ev',
                        label: useI18n().t('energieverbrauch'),
                    },
                ],
            },
            {
                label: useI18n().t('produktion'),
                visible: authUserHasRole(ROLES.berechnete_listen),
                icon: 'fa-solid fa-industry',
                key: 'ber_prod',
                class: 'nav-layer-2',
                items: [
                    {
                        route: 'ber_prod_sa',
                        visible: authUserHasRole(ROLES.berechnete_listen),
                        key: 'ber_prod_sa',
                        label: useI18n().t('stromerzeugungsanlagen'),
                    },
                ],
            },
        ],
    },
    {
        label: useI18n().t('auswertungen'),
        visible: authUserHasRole(ROLES.auswertungen),
        icon: 'fa-solid fa-file-chart-pie',
        key: 'aus',
        items: [
            {
                route: 'aus_geb',
                visible: authUserHasRole(ROLES.auswertungen),
                key: 'aus_geb',
                label: useI18n().t('gebaeude'),
                icon: 'fa-solid fa-house',
                class: 'selectable-header',
            },
            {
                label: useI18n().t('produktion'),
                visible: authUserHasRole(ROLES.auswertungen),
                key: 'aus_prod',
                route: 'aus_prod',
                icon: 'fa-solid fa-industry',
                class: 'selectable-header',
            },
        ],
    },
    {
        label: useI18n().t('system'),
        visible: authUserHasRole(ROLES.systemeinstellungen),
        icon: 'fa-solid fa-gear',
        key: 'system',
        items: [
            {
                visible: authUserHasRole(ROLES.systemeinstellungen),
                route: 'system_rolle',
                key: 'system_rolle',
                label: useI18n().t('rollen'),
                icon: 'fa-solid fa-users',
                class: 'selectable-header',
            },
            {
                visible: authUserHasRole(ROLES.systemeinstellungen),
                route: 'system_benutzer',
                key: 'system_benutzer',
                label: useI18n().t('benutzer'),
                icon: 'fa-solid fa-user',
                class: 'selectable-header',
            },
            {
                route: 'test_view',
                key: 'test_view',
                label: 'Play Ground',
                icon: 'fa-solid fa-balloons',
                class: 'selectable-header',
                visible: false,
            },
        ],
    },
]);

const router = useRouter();
const route = useRoute();
const activeElement: Ref<string | undefined> = ref(undefined);

onMounted(async () => {
    await router.isReady();
    fillExpandedKeys(route.name);
});

async function fillExpandedKeys(name: RouteRecordNameGeneric) {
    if (name && name.toString().endsWith('_edit')) {
        name = name.toString().substring(0, name.toString().length - 5);
    }
    iterateMenu(menu.value, name);
}

function iterateMenu(item: MenuItem, name: RouteRecordNameGeneric) {
    let found = false;
    item.forEach((entry: MenuItem) => {
        if (entry.items) {
            if (iterateMenu(entry.items, name) && entry.key) {
                expandedKeys.value = { ...expandedKeys.value, [entry.key]: true };
                found = true;
            }
        } else if (entry.route === name) {
            found = true;
            activeElement.value = entry.key;
        }
    });
    return found;
}

function onExpansionChange(event: any) {
    expandedKeys.value = event;
}

const foo = (state: any) => {
    console.log(state);
};
function clickediClick(item: MenuItem) {
    activeElement.value = item.key;
}
</script>
<template>
    <div class="card nav-card">
        <div class="w-full" v-for="(items, idx) in menu" :key="idx">
            <div v-if="items.visible" class="menu-title">{{ items.label }}</div>
            <PanelMenu
                :model="items.items"
                :expanded-keys="expandedKeys"
                @update:expandedKeys="onExpansionChange"
                unstyled
                :pt="{
                    itemContent: ({ context }) => [
                        {
                            'menu-item-content': context.item.route,
                            'active-item': context.item.key === activeElement,
                        },
                    ],
                    root: {
                        class: 'menu-section',
                    },
                    rootList: {
                        class: 'menu-rootlist',
                    },
                    submenu: {
                        class: 'menu-rootlist',
                    },
                    headerContent: {
                        class: 'menu-header',
                    },
                    item: {
                        class: 'menu-item',
                    },
                    headerIcon: {
                        class: 'menu-icon',
                    },
                    Icon: {
                        class: 'menu-icon',
                    },
                }"
            >
                <template #item="{ item }">
                    <router-link
                        v-if="item.route"
                        v-slot="{ href, navigate }"
                        :to="{ name: item.route }"
                        custom
                        @click="clickediClick(item)"
                        :class="{
                            'p-0_5rem': true,
                            'active-header-item': item.key === activeElement && item.class === 'selectable-header',
                        }"
                    >
                        <a class="flex items-center cursor-pointer" :href="href" @click="navigate">
                            <span v-if="item.icon" class="mr-1rem w-1rem"><i :class="item.icon"></i></span>
                            <span v-else class="mr-2rem w-0_5rem"></span>
                            <span>{{ item.label }}</span>
                        </a>
                    </router-link>
                    <a v-else class="p-0_5rem flex items-center cursor-pointer">
                        <span v-if="item.icon" class="mr-1rem w-1rem"><i :class="item.icon"></i></span>
                        <span v-else class="mr-2rem w-0_5rem"></span>
                        <span>{{ item.label }}</span>
                        <i v-if="item.items" class="far fa-chevron-down ml-auto"></i>
                    </a>
                </template>
            </PanelMenu>
        </div>
    </div>
</template>

<style lang="css" scoped>
.nav-card {
    background: #f5f5f5;
}
.nav-header {
    background: var(--p-primary-200) !important;
    a {
        color: var(--p-text-color);
        padding-left: 3.5rem !important;
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
    }
}
:deep(.nav-layer-2) {
    background: #ffffff;
    border-radius: 0.3rem;
    margin: 0.5rem 0;
}
:deep(.nav-layer-2 a) {
    padding-left: 1rem;
}

:deep(.selectable-header) {
    background: #ffffff;
    border-radius: 0.3rem;
    margin: 0.5rem 0;
    a {
        padding-left: 1rem;
    }
    :hover {
        background-color: var(--p-amber-100);
    }
}

:deep(.menu-section) {
    margin-left: 1rem;
}

:deep(.menu-rootlist) {
    padding: 0;
    list-style: none;
    margin: 0;
}
:deep(.menu-item-content:hover) {
    background: var(--p-amber-100);
}

:deep(.menu-item-content a) {
    padding: 0.4rem 0 0.4rem 0.5rem;
    color: var(--p-text-color);
    text-decoration: none;
}

:deep(.menu-item-content) {
    border-left: 0.5rem solid transparent !important;
}

:deep(.menu-item-header) {
    background: var(--p-primary-50);
    a {
        color: var(--p-text-color);
        text-decoration: none;
    }
}
:deep(.menu-header) {
    a {
        color: var(--p-text-color);
        text-decoration: none;
    }
}

:deep(.active-item) {
    background: var(--p-amber-100);
    border-left: 0.5rem solid var(--p-amber-500) !important;
    font-weight: 800;
}

:deep(.active-header-item) {
    border-left: 0.5rem solid var(--p-amber-500) !important;
    border-radius: 0.3rem;
    background: var(--p-amber-100);
    font-weight: 800;
    padding-left: 0.5rem !important;
}

.menu-title {
    padding: 2rem 0 0 1rem;
    font-weight: 600;
}
</style>
